import React, { useEffect, useRef, useState } from 'react';
import './RegistrationPage.css';
import { useNavigate, useSearchParams } from 'react-router-dom';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SESSION_DURATION = 5 * 60; // seconds

export default function MfaVerifyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const sessionToken = searchParams.get('session_token');
  const state = searchParams.get('state');

  // Forward to backend /mfa/begin when arriving fresh from Auth0
  useEffect(() => {
    if (sessionToken && state) {
      window.location.href =
        `${BACKEND}/mfa/begin` +
        `?session_token=${encodeURIComponent(sessionToken)}` +
        `&state=${encodeURIComponent(state)}`;
    }
  }, []);

  const [sessionCountdown, setSessionCountdown] = useState(SESSION_DURATION);
  const timedOutRef = useRef(false);

  const [otp, setOtp] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (sessionCountdown <= 0) {
      if (!timedOutRef.current) {
        timedOutRef.current = true;
        handleTimeout();
      }
      return;
    }
    const id = setTimeout(() => setSessionCountdown((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [sessionCountdown]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const id = setTimeout(() => setResendCountdown((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCountdown]);

  // Auto-send OTP once the session is established (no session_token in URL)
  useEffect(() => {
    if (!sessionToken) {
      sendOtp(true);
    }
  }, []);

  async function apiPost(path: string, body: object = {}): Promise<any> {
    const res = await fetch(`${BACKEND}/mfa/${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err: any = new Error(data.message || 'Something went wrong.');
      err.status = res.status;
      throw err;
    }
    return data;
  }

  async function handleTimeout() {
    try {
      await fetch(`${BACKEND}/mfa/timeout`, { method: 'POST', credentials: 'include' });
    } catch {}
    navigate('/mfa-expired');
  }

  function formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  async function sendOtp(isMount = false) {
    setLoading(true);
    try {
      await apiPost('send-otp');
    } catch (err: any) {
      if (err.status === 401) { navigate('/mfa-expired'); return; }
      if (!isMount) setError(err.message);
    } finally {
      setResendCountdown(30);
      setLoading(false);
    }
  }

  async function handleOtpChange(val: string) {
    const digits = val.replace(/\D/g, '').slice(0, 6);
    setOtp(digits);
    setError('');
    if (digits.length < 6) return;
    setLoading(true);
    try {
      const data = await apiPost('verify-otp', { otp: digits });
      window.location.href = data.continueUrl;
    } catch (err: any) {
      if (err.status === 401) { navigate('/mfa-expired'); return; }
      setError(err.message);
      setOtp('');
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (resendCountdown > 0) return;
    setError('');
    await sendOtp();
  }

  // While the /mfa/begin redirect is in flight, render nothing
  if (sessionToken) return null;

  return (
    <div className="registration-page-container">
      <div className="registration-form-user">
        <div className="mfa-session-timer" style={{ textAlign: 'center', marginBottom: 8, fontSize: 13, color: sessionCountdown < 60 ? '#c0392b' : '#555' }}>
          Session expires in: {formatTime(sessionCountdown)}
        </div>

        <div className="reg-stage-form">
          <h2>Verify your number</h2>
          <p className="reg-stage-subtext">
            We sent a 6-digit code to your registered mobile number.
          </p>
          <input
            className="reg-otp-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="——————"
            maxLength={6}
            value={otp}
            onChange={(e) => handleOtpChange(e.target.value)}
            disabled={loading}
            autoFocus
          />
          {error && <div className="error-message">{error}</div>}
          <div className="reg-resend-text">
            {resendCountdown > 0 ? (
              <span className="reg-resend-countdown">Resend in {resendCountdown}s</span>
            ) : (
              <span className="reg-resend-link" onClick={handleResend}>Resend code</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
